import { CommonResponse, ErrorResponse } from "@boi/backend-utils";
import { ChapterReqModel, ClassIdRequestModel, ClassRequestModel, ClassSubjectReq, ClassSubjectReqModel, ClonePaperReq, CreateQuestionPaperReq, DefaultModelPaperCheckReqModel, PaperGenMethodEnum, PaperPreviewModel, PaperPreviewSectionsModel, QNPIdReq, QPaperMergeCreateReqModel, QPaperMergeGerReqModel, QnosReq, QuestionReqModel, QuestionsModel, SetAndQuestionMappingsModel, SubjectClassReqModel, qPRequestModel } from "@boi/shared-models";
import { WhatsAppNotificationService } from "@boi/shared-services";
import { Injectable } from "@nestjs/common";
import ShortUniqueId from 'short-unique-id';
import { DataSource, In } from "typeorm";
import { GenericTransactionManager } from "../../database/typeorm-transactions";
import { TabQuestionRepo } from "../create-questions/repos/tab-question.repo";
import { ModelPaperRepo } from "../model-paper/repositories/model-paper-repo";
import { SectionQuestionsMappingAdapter } from "./dtos/section-questions-mapping.adaptr";
import { UserToQnosMappingEntity } from "./entities/user-to-qnos-mapping.entity";
import { ChapterRepo } from "./repo/chapter-repo";
import { ClassRepo } from "./repo/class-repo";
import { ComplexityRepo } from "./repo/complexity-repo";
import { CreateQuestionRepo } from "./repo/create-question-repo";
import { LanguagesRepo } from "./repo/languages-repo";
import { QuestionPapersRepo } from "./repo/question-papers-repo";
import { QuestionTypeRepo } from "./repo/question-type-repo";
import { SectionWiseModelRepo } from "./repo/section-wise-model-repo";
import { SubjectRepo } from "./repo/subject-repo";
import { TopicRepo } from "./repo/topic-repo";
import { QuestionPaperModal } from "../model-paper/entities/question-paper-modal";
import { PaperMergeRepo } from "./repo/paper-merge.repo";
import { PaperMergeChildRepo } from "./repo/paper-merge-child.repo";
import { PaperMergeEntity } from "./entities/paper-merge.entity";
import { PaperMergeChildEntity } from "./entities/paper-merge-child.entity";
import { AbilityRepo } from "./repo/ability-repo";
import { AbilityTypeRepo } from "./repo/ability-type-repo";
import { QuestionTypeMasterRepo } from "./repo/question-type-master-repo";
import { CompetencyCodeRepo } from "./repo/competency-code-repo";


@Injectable()
export class AiGeneratedQpService {
    constructor(
        private dataSource: DataSource,
        private classRepo: ClassRepo,
        private subjectRepo: SubjectRepo,
        private chapterRepo: ChapterRepo,
        private topicRepo: TopicRepo,
        private complexityRepo: ComplexityRepo,
        private languagesRepo: LanguagesRepo,
        private questionType: QuestionTypeRepo,
        private createQuestionRepo: CreateQuestionRepo,
        private modelPaperRepo: ModelPaperRepo,
        private questionPapersRepo: QuestionPapersRepo,
        private sectionWiseModelRepo: SectionWiseModelRepo,
        private adapter: SectionQuestionsMappingAdapter,
        private wtsAppMessage: WhatsAppNotificationService,
        private tabQuestionRepo: TabQuestionRepo,
        private paperMergeRepo: PaperMergeRepo,
        private paperMergeChildRepo: PaperMergeChildRepo,
        private abilityRepo: AbilityRepo,
        private abilityTypeRepo: AbilityTypeRepo,
        private questionTypeMasterRepo: QuestionTypeMasterRepo,
        private competencyCodeRepo: CompetencyCodeRepo,
    ) { }

    private readonly DIFFICULTY_EASY = 1;
    private readonly DIFFICULTY_MODERATE = 2;
    private readonly DIFFICULTY_HARD = 3;

    async distributeDifficulty(
        total: number,
        easyPct: number,
        moderatePct: number,
        difficultPct: number
    ): Promise<{ easy: number; moderate: number; difficult: number }> {

        const rawEasy = total * (easyPct / 100);
        const rawModerate = total * (moderatePct / 100);
        const rawDifficult = total * (difficultPct / 100);

        let countEasy = Math.floor(rawEasy);
        let countModerate = Math.floor(rawModerate);
        let countDifficult = Math.floor(rawDifficult);

        const remainder = total - (countEasy + countModerate + countDifficult);

        if (remainder > 0) {
            const buckets = [
                { key: 'easy', decimal: rawEasy - countEasy },
                { key: 'moderate', decimal: rawModerate - countModerate },
                { key: 'difficult', decimal: rawDifficult - countDifficult },
            ].sort((a, b) => b.decimal - a.decimal);

            for (let i = 0; i < remainder; i++) {
                if (buckets[i].key === 'easy') countEasy++;
                else if (buckets[i].key === 'moderate') countModerate++;
                else countDifficult++;
            }
        }

        return { easy: countEasy, moderate: countModerate, difficult: countDifficult };
    }

    private pickDifficulty(remaining) {
        const entries = [
            { key: 'difficult', value: remaining.difficult },
            { key: 'moderate', value: remaining.moderate },
            { key: 'easy', value: remaining.easy }
        ];

        entries.sort((a, b) => b.value - a.value);

        return entries[0].key;
    }

    async getAllClasses(): Promise<CommonResponse> {
        const result = await this.classRepo.getAllClasses();
        return new CommonResponse(true, 123, 'classes retrieved successfully', result);
    }

    async getLanguagesByClassSubject(req: ClassSubjectReq): Promise<CommonResponse> {
        const subjectName = await this.subjectRepo.findOne({ select: ['id', 'subject'], where: { id: req.subject } });
        const result = await this.subjectRepo.getLanguagesByClassSubject(subjectName.subject, req.classId);
        return new CommonResponse(true, result.length, 'Subjects and Languages retrieved successfully', result);
    }

    async getAllLanguages(): Promise<CommonResponse> {
        const result = await this.languagesRepo.getAllLanguages();
        return new CommonResponse(true, 123, 'Languages retrieved successfully', result);
    }

    async getSubjectsByClass(req: ClassRequestModel): Promise<CommonResponse> {
        const result = await this.subjectRepo.getSubjectsByClass(req);
        return new CommonResponse(true, 123, 'Subjects retrieved successfully', result);
    }

    async getChaptersBySubject(req: SubjectClassReqModel): Promise<CommonResponse> {
        const result = await this.chapterRepo.getChaptersBySubject(req);
        return new CommonResponse(true, 123, 'Chapters retrieved Successfully', result)
    }

    async getDistinctSubjects(className?: string): Promise<CommonResponse> {
        const result = await this.tabQuestionRepo.getDistinctSubjects(className);
        return new CommonResponse(true, 123, 'Subjects retrieved successfully', result);
    }

    async getSubjectByClass(classId?: number): Promise<CommonResponse> {
        const result = await this.subjectRepo.getSubjectByClass(classId);
        return new CommonResponse(true, 123, 'Subjects retrieved successfully', result);
    }

    // async getSubjectByClass(classId?: number): Promise<CommonResponse> {
    //     console.log('Received classId:', classId);

    //     const result = await this.subjectRepo.getSubjectByClass(classId);
    //     console.log('Query result:', result);

    //     return new CommonResponse(true, 123, 'Subjects retrieved successfully', result);
    // }

    async getDistinctChapters(className?: string): Promise<CommonResponse> {
        const result = await this.tabQuestionRepo.getDistinctChapters(className);
        return new CommonResponse(true, 123, 'Chapters retrieved successfully', result);
    }

    async getChaptersBySubjectAndClass(req: SubjectClassReqModel): Promise<CommonResponse> {
        const result = await this.chapterRepo.getChaptersBySubjectAndClass(req);
        return new CommonResponse(true, 123, 'Chapters retrieved successfully', result);
    }

    async getDistinctMarks(className?: string): Promise<CommonResponse> {
        const result = await this.tabQuestionRepo.getDistinctMarks(className);
        return new CommonResponse(true, 123, 'Marks retrieved successfully', result);
    }


    async getDistinctMarksFromCreateQuestions(req: ChapterReqModel): Promise<CommonResponse> {
        const result = await this.createQuestionRepo.getDistinctMarksFromCreateQuestions(req);
        return new CommonResponse(true, 123, 'Marks retrieved successfully', result);
    }

    async getMarksByClass(classId?: number): Promise<CommonResponse> {
        const result = await this.createQuestionRepo.getMarksByClass(classId);
        return new CommonResponse(true, 123, 'Marks retrieved successfully', result);
    }

    async getChaptersByClassAndSubject(req: ClassSubjectReqModel): Promise<CommonResponse> {
        const result = await this.tabQuestionRepo.getChaptersByClassAndSubject(req);
        return new CommonResponse(true, 123, 'Chapters retrieved successfully', result);
    }


    async getTopicsByChapter(req: ChapterReqModel): Promise<CommonResponse> {
        const result = await this.topicRepo.getTopicsByChapter(req.chapter);
        return new CommonResponse(true, 123, 'Chapters retrieved Successfully', result)
    }

    async getMarksByChapter(req: ChapterReqModel): Promise<CommonResponse> {
        const result = await this.createQuestionRepo.getMarksByChapter(req);
        return new CommonResponse(true, 123, 'Chapters retrieved Successfully', result)
    }

    async getAllDifficultyLevels(): Promise<CommonResponse> {
        const result = await this.complexityRepo.find({ select: ['id', 'complexity'] });
        return new CommonResponse(true, 123, 'Difficulty levels retrieved Successfully', result)
    }

    async getAllQuestionTypes(): Promise<CommonResponse> {
        const result = await this.questionType.find({ select: ['id', 'questiontype'] });
        return new CommonResponse(true, 123, 'All Question Types retrieved Successfully', result)
    }

    async getAllAbilities(): Promise<CommonResponse> {
        const result = await this.abilityRepo.find({ select: ['id', 'type'] });
        return new CommonResponse(true, 123, 'All Abilities retrieved Successfully', result)
    }

    async getAbilityTypesByAbilityId(abilityId: number): Promise<CommonResponse> {
        console.log("ability", abilityId)
        const result = await this.abilityTypeRepo.find({ select: ['id', 'types'], where: { abilityId } });
        return new CommonResponse(true, 123, 'Ability Types retrieved Successfully', result)
    }

    async getAllAbilityTypes(): Promise<CommonResponse> {
        const result = await this.abilityTypeRepo.find({ select: ['id', 'types', 'abilityId'] });
        return new CommonResponse(true, 123, 'All Ability Types retrieved Successfully', result)
    }

    async getAllQuestionTypeMaster(): Promise<CommonResponse> {
        const result = await this.questionTypeMasterRepo.find({ select: ['id', 'type'] });
        return new CommonResponse(true, 123, 'All Question Type Master retrieved Successfully', result)
    }

    async getAllCompetencyCodes(): Promise<CommonResponse> {
        const result = await this.competencyCodeRepo.find({ select: ['id', 'code'] });
        return new CommonResponse(true, 123, 'All Competency Codes retrieved Successfully', result)
    }

    async getAllQuestions(req: QuestionReqModel, select = []): Promise<CommonResponse> {
        const where = {
            class: req.className,
            subject: req.subject,
            chapter: req.chapter,
            isActive: true
        }

        if (req.topic) {
            where['topic'] = req.topic
        }

        if (req.difficultyLevel) {
            where['complexity'] = req.difficultyLevel
        }

        if (req.questionType) {
            where['questiontype'] = req.questionType
        }

        if (req.weightage) {
            where['marks'] = req.weightage
        }

        // console.log("difficultyLevel", req.difficultyLevel)

        if (req.subAbilityId) {
            where['abilityType'] = req.subAbilityId
        }

        // if (req.subAbilityId && req.subAbilityId > 0) {
        //     where['abilityType'] = req.subAbilityId
        //     console.log(`Filtering by SubAbility: ${req.subAbilityId}`);
        // } else {
        //     console.log(`No SubAbility filter applied - fetching all questions regardless of ability type`);
        // }

        const result = await this.createQuestionRepo.find({
            select,
            where
        });
        return new CommonResponse(true, 123, 'All Questions retrieved Successfully', result)
    }

    async getAllQuestionsCount(req: QuestionReqModel): Promise<CommonResponse> {
        const where = {
            class: req.className,
            subject: req.subject,
            chapter: req.chapter,
            isActive: true
        }
        if (req.topic) {
            where['topic'] = req.topic
        }
        if (req.difficultyLevel) {
            where['complexity'] = req.difficultyLevel
        }
        if (req.questionType) {
            where['questiontype'] = req.questionType
        }


        if (req.weightage) {
            where['marks'] = req.weightage
        }
        const result = await this.createQuestionRepo.count({
            where
        });

        return new CommonResponse(true, 1234, 'All Questions retrieved Successfully', result)
    }

    shuffleArray = (array) => {
        const shuffledArray = [...array];
        for (let i = shuffledArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
        }
        return shuffledArray;
    };

    // async createQuestionPaper(req: CreateQuestionPaperReq): Promise<CommonResponse> {
    //     const transactionalEntityManager = new GenericTransactionManager(this.dataSource);
    //     try {
    //         const entitiesToSave = [];
    //         const { randomUUID } = new ShortUniqueId({ length: 10 });
    //         const paperBundlerId = randomUUID(20);
    //         for (const set of Array.from({ length: req.noOfSets }, (_, index) => index + 1)) {
    //             const questionAndSerialNumbersMap = new Map();
    //             const sets = req.generationMethod === PaperGenMethodEnum.SYSTEM ? this.adapter.getSectionMappingsOfSection(req.sectionQuestionMappings, 1) : this.adapter.getSectionMappingsOfSection(req.sectionQuestionMappings, set);
    //             for (const sections of sets.sectionsMapping) {
    //                 if (req.generationMethod === PaperGenMethodEnum.SYSTEM) {
    //                     const questions = await this.getAllQuestions(sections, ['qno']);
    //                     const questionsIds = this.shuffleArray(questions.data.map(rec => rec.qno));
    //                     const result = new Set<number>()
    //                     while (result.size < sections.noOfQns) {
    //                         const randomInt = Math.floor(Math.random() * (questionsIds.length - 0 + 1)) + 0;
    //                         if (questionsIds[randomInt]) {
    //                             if (!result.has(questionsIds[randomInt]) && !questionAndSerialNumbersMap.has(questionsIds[randomInt])) {
    //                                 result.add(questionsIds[randomInt]);
    //                                 questionAndSerialNumbersMap.set(questionsIds[randomInt], { serialQno: questionAndSerialNumbersMap.size, qno: questionsIds[randomInt] });
    //                             }
    //                         }
    //                     }
    //                     sections.qnos = Array.from(result).toString();
    //                 } else {
    //                     const qnos = sections.qnos.split(',');
    //                     qnos.forEach((qno) => {
    //                         if (!questionAndSerialNumbersMap.has(qno)) {
    //                             questionAndSerialNumbersMap.set(qno, { serialQno: questionAndSerialNumbersMap.size, qno: Number(qno) });
    //                         }
    //                     })
    //                 }
    //             }
    //             req.noOfSets = set;
    //             req.paperBundlerId = paperBundlerId;
    //             const entity = this.adapter.convertDtoToEntity(req, Array.from(questionAndSerialNumbersMap.values()));
    //             entitiesToSave.push(entity);
    //             if (req.generateOtherLanguagePapers) {
    //                 const subjectName = await this.subjectRepo.findOne({ select: ['id', 'subject', 'class'], where: { id: req.subject } });
    //                 const result = await this.subjectRepo.getOtherLanguageSubjectIds(subjectName.subject, subjectName.class, req.subject);
    //                 const mainModelPaperIdData = await this.modelPaperRepo.findOne({ where: { id: req.qnpModelId } });
    //                 console.log(mainModelPaperIdData, 'mainModelPaperIdData');

    //                 for (const otherSubject of result) {
    //                     const allQnosSet = new Set();
    //                     console.log(otherSubject, 'otherSubject');
    //                     const otherModel = await this.modelPaperRepo.findOne({ where: { class: subjectName.class, subject: otherSubject.subjectId, language: otherSubject.languageId, mainModelPaperId: mainModelPaperIdData.mainModelPaperId } });
    //                     console.log(otherModel, 'otherModel');

    //                     const otherSubReq: CreateQuestionPaperReq = JSON.parse(JSON.stringify(req));
    //                     otherSubReq.subject = otherSubject.subjectId;
    //                     otherSubReq.qnpModelId = otherModel.id;
    //                     console.log(otherSubReq, 'otherSubReq');
    //                     console.log(req);
    //                     const sectionMappings = [...sets.sectionsMapping];
    //                     for (const sec of sectionMappings) {
    //                         const mainQnpModel = new QuestionPaperModal();
    //                         mainQnpModel.id = req.qnpModelId;
    //                         const mainSection = await this.sectionWiseModelRepo.findOne({ where: { id: sec.sectionId } });
    //                         const sectionId = await this.sectionWiseModelRepo.findOne({ where: { qnpModel: otherModel, eachQnWeightage: sec.weightage, sectionNo: mainSection.sectionNo } });
    //                         const qnoArray = sec.qnos.split(',');
    //                         const result = await this.createQuestionRepo.find({ where: { qno: In([null, ...qnoArray]), subject: otherSubject.subjectId, ...(sec.subAbilityId && { subAbilityId: sec.subAbilityId }) } });
    //                         if (result.length === qnoArray.length) {
    //                             sec.chapter = result[0].chapter;
    //                             sec.className = result[0].class;
    //                             sec.sectionId = sectionId.id;
    //                             sec.subject = otherSubject.subjectId;
    //                             sec.language = otherSubject.languageId;
    //                             allQnosSet.add(result.length === qnoArray.length);
    //                         }
    //                     }
    //                     otherSubReq.sectionQuestionMappings = [new SetAndQuestionMappingsModel(1, sectionMappings)];
    //                     if (allQnosSet.size === 1 && allQnosSet.has(true)) {
    //                         const entity = this.adapter.convertDtoToEntity(otherSubReq, Array.from(questionAndSerialNumbersMap.values()));
    //                         entitiesToSave.push(entity);
    //                     }
    //                 }
    //             }
    //         }
    //         const savedEntity = await this.questionPapersRepo.save(entitiesToSave);
    //         return new CommonResponse(true, 1234, 'Paper Generated Successfully', savedEntity);
    //     } catch (err) {
    //         transactionalEntityManager ? await transactionalEntityManager.releaseTransaction() : '';
    //         throw err;
    //     }
    // }






    private sortByDifficultyPriority(
        questions: { qno: number; complexity: number }[],
        easyBudget: number,
        moderateBudget: number,
        hardBudget: number,
        hasDifficulty: boolean,
    ): { qno: number; complexity: number }[] {
        if (!hasDifficulty) return questions;

        const priorityOf = (complexity: number): number => {
            if (complexity === this.DIFFICULTY_EASY) return easyBudget;
            if (complexity === this.DIFFICULTY_MODERATE) return moderateBudget;
            if (complexity === this.DIFFICULTY_HARD) return hardBudget;
            return 0;
        };

        return [...questions].sort((a, b) => priorityOf(b.complexity) - priorityOf(a.complexity));
    }




    // async createQuestionPaper(req: CreateQuestionPaperReq): Promise<CommonResponse> {
    //     const transactionalEntityManager = new GenericTransactionManager(this.dataSource);
    //     try {
    //         const entitiesToSave = [];
    //         const { randomUUID } = new ShortUniqueId({ length: 10 });
    //         const paperBundlerId = randomUUID(20);

    //         // Pre-calculate total paper marks from blueprint (eachQnWeightage × noOfQns summed across sections)
    //         const totalPaperMarks = req.sectionQuestionMappings[0].sectionsMapping.reduce(
    //             (sum, sec) => sum + sec.weightage * sec.noOfQns, 0
    //         );

    //         console.log("totalPaperMarks", totalPaperMarks)

    //         const hasDifficulty =
    //             req.difficulty &&
    //             (req.difficulty.easy > 0 || req.difficulty.moderate > 0 || req.difficulty.difficult > 0);

    //         for (const set of Array.from({ length: req.noOfSets }, (_, index) => index + 1)) {
    //             const questionAndSerialNumbersMap = new Map<number, { serialQno: number; qno: number }>();

    //             const sets =
    //                 req.generationMethod === PaperGenMethodEnum.SYSTEM
    //                     ? this.adapter.getSectionMappingsOfSection(req.sectionQuestionMappings, 1)
    //                     : this.adapter.getSectionMappingsOfSection(req.sectionQuestionMappings, set);

    //             // --- Difficulty budget for this set ---
    //             let easyBudget = 0;
    //             let moderateBudget = 0;
    //             let hardBudget = 0;

    //             if (hasDifficulty) {
    //                 const dist = await this.distributeDifficulty(
    //                     totalPaperMarks,
    //                     req.difficulty.easy,
    //                     req.difficulty.moderate,
    //                     req.difficulty.difficult,
    //                 );
    //                 easyBudget = dist.easy;
    //                 moderateBudget = dist.moderate;
    //                 hardBudget = dist.difficult;
    //                 console.log(`Set ${set} difficulty budget → easy:${easyBudget} moderate:${moderateBudget} hard:${hardBudget}`);
    //             }

    //             for (const sections of sets.sectionsMapping) {
    //                 if (req.generationMethod === PaperGenMethodEnum.SYSTEM) {
    //                     // Fetch all questions for this bucket (no difficulty filter — we pick by priority below)
    //                     const questions = await this.getAllQuestions(sections, ['qno', 'complexity']);
    //                     const allQuestions: { qno: number; complexity: number }[] = questions.data;

    //                     console.log("data of the quesions format",
    //                         allQuestions.reduce((acc, q) => {
    //                             acc[q.complexity] = (acc[q.complexity] || 0) + 1;
    //                             return acc;
    //                         }, {})
    //                     )

    //                     if (allQuestions.length < sections.noOfQns) {
    //                         throw new Error(
    //                             `Insufficient questions! Required: ${sections.noOfQns}, Available: ${allQuestions.length} ` +
    //                             `for Chapter: ${sections.chapter}, Marks: ${sections.weightage}, SubAbilityId: ${sections.subAbilityId || 'Any'}`,
    //                         );
    //                     }

    //                     const qnMarks = sections.weightage; // every question in this bucket has the same mark value

    //                     // Sort questions by difficulty priority based on remaining budget
    //                     const sorted = this.sortByDifficultyPriority(allQuestions, easyBudget, moderateBudget, hardBudget, hasDifficulty);
    //                     const shuffledWithinPriority = this.shuffleWithinPriorityGroups(sorted, easyBudget, moderateBudget, hardBudget, hasDifficulty);

    //                     const result = new Set<number>();

    //                     for (const q of shuffledWithinPriority) {
    //                         if (result.size >= sections.noOfQns) break;
    //                         if (questionAndSerialNumbersMap.has(q.qno)) continue;

    //                         result.add(q.qno);
    //                         questionAndSerialNumbersMap.set(q.qno, {
    //                             serialQno: questionAndSerialNumbersMap.size + 1,
    //                             qno: q.qno,
    //                         });

    //                         // Deduct from the correct budget
    //                         if (hasDifficulty) {
    //                             if (q.complexity === this.DIFFICULTY_EASY) {
    //                                 easyBudget -= qnMarks;
    //                             } else if (q.complexity === this.DIFFICULTY_MODERATE) {
    //                                 moderateBudget -= qnMarks;
    //                             } else if (q.complexity === this.DIFFICULTY_HARD) {
    //                                 hardBudget -= qnMarks;
    //                             }
    //                         }
    //                     }

    //                     if (result.size < sections.noOfQns) {
    //                         throw new Error(
    //                             `Not enough unused questions remaining to generate set ${set} for Chapter: ${sections.chapter}`
    //                         );
    //                     }

    //                     sections.qnos = Array.from(result).toString();

    //                 } else {
    //                     // MANUAL mode — qnos are pre-specified
    //                     const qnos = sections.qnos.split(',');
    //                     qnos.forEach((qno) => {
    //                         const qnoNumber = Number(qno);
    //                         if (!questionAndSerialNumbersMap.has(qnoNumber)) {
    //                             questionAndSerialNumbersMap.set(qnoNumber, {
    //                                 serialQno: questionAndSerialNumbersMap.size + 1,
    //                                 qno: qnoNumber,
    //                             });
    //                         }
    //                     });
    //                 }
    //             }

    //             // Validate difficulty deviation after all sections picked (only in SYSTEM mode)
    //             if (hasDifficulty && req.generationMethod === PaperGenMethodEnum.SYSTEM) {
    //                 const tolerance = 2;
    //                 const easyDeviation = Math.abs(easyBudget);
    //                 const moderateDeviation = Math.abs(moderateBudget);
    //                 const hardDeviation = Math.abs(hardBudget);

    //                 console.log(
    //                     `Set ${set} difficulty deviation → easy:${easyBudget} moderate:${moderateBudget} hard:${hardBudget}`
    //                 );

    //                 if (easyDeviation > tolerance || moderateDeviation > tolerance || hardDeviation > tolerance) {
    //                     throw new Error(
    //                         `Set ${set} difficulty targets not satisfiable within ±${tolerance} marks tolerance. ` +
    //                         `Remaining budgets → easy:${easyBudget}, moderate:${moderateBudget}, hard:${hardBudget}`
    //                     );
    //                 }
    //             }

    //             req.noOfSets = set;
    //             req.paperBundlerId = paperBundlerId;

    //             const entity = this.adapter.convertDtoToEntity(
    //                 req,
    //                 Array.from(questionAndSerialNumbersMap.values()),
    //             );
    //             entitiesToSave.push(entity);

    //             // --- Other language papers ---
    //             if (req.generateOtherLanguagePapers) {
    //                 const subjectName = await this.subjectRepo.findOne({
    //                     select: ['id', 'subject', 'class'],
    //                     where: { id: req.subject },
    //                 });
    //                 const result = await this.subjectRepo.getOtherLanguageSubjectIds(
    //                     subjectName.subject,
    //                     subjectName.class,
    //                     req.subject,
    //                 );
    //                 const mainModelPaperIdData = await this.modelPaperRepo.findOne({
    //                     where: { id: req.qnpModelId },
    //                 });

    //                 for (const otherSubject of result) {
    //                     const allQnosSet = new Set();
    //                     const otherModel = await this.modelPaperRepo.findOne({
    //                         where: {
    //                             class: subjectName.class,
    //                             subject: otherSubject.subjectId,
    //                             language: otherSubject.languageId,
    //                             mainModelPaperId: mainModelPaperIdData.mainModelPaperId,
    //                         },
    //                     });

    //                     const otherSubReq: CreateQuestionPaperReq = JSON.parse(JSON.stringify(req));
    //                     otherSubReq.subject = otherSubject.subjectId;
    //                     otherSubReq.qnpModelId = otherModel.id;

    //                     const sectionMappings = [...sets.sectionsMapping];
    //                     for (const sec of sectionMappings) {
    //                         const mainSection = await this.sectionWiseModelRepo.findOne({
    //                             where: { id: sec.sectionId },
    //                         });
    //                         const sectionId = await this.sectionWiseModelRepo.findOne({
    //                             where: {
    //                                 qnpModel: otherModel,
    //                                 eachQnWeightage: sec.weightage,
    //                                 sectionNo: mainSection.sectionNo,
    //                             },
    //                         });
    //                         const qnoArray = sec.qnos.split(',');
    //                         const otherLangQuestions = await this.createQuestionRepo.find({
    //                             where: {
    //                                 qno: In([null, ...qnoArray]),
    //                                 subject: otherSubject.subjectId,
    //                                 ...(sec.subAbilityId && { abilityType: sec.subAbilityId }),
    //                             },
    //                         });

    //                         if (otherLangQuestions.length === qnoArray.length) {
    //                             sec.chapter = otherLangQuestions[0].chapter;
    //                             sec.className = otherLangQuestions[0].class;
    //                             sec.sectionId = sectionId.id;
    //                             sec.subject = otherSubject.subjectId;
    //                             sec.language = otherSubject.languageId;
    //                             allQnosSet.add(true);
    //                         }
    //                     }

    //                     otherSubReq.sectionQuestionMappings = [
    //                         new SetAndQuestionMappingsModel(1, sectionMappings),
    //                     ];

    //                     if (allQnosSet.size === 1 && allQnosSet.has(true)) {
    //                         const otherEntity = this.adapter.convertDtoToEntity(
    //                             otherSubReq,
    //                             Array.from(questionAndSerialNumbersMap.values()),
    //                         );
    //                         entitiesToSave.push(otherEntity);
    //                     }
    //                 }
    //             }
    //         }

    //         const savedEntity = await this.questionPapersRepo.save(entitiesToSave);
    //         return new CommonResponse(true, 1234, 'Paper Generated Successfully', savedEntity);
    //     } catch (err) {
    //         transactionalEntityManager
    //             ? await transactionalEntityManager.releaseTransaction()
    //             : '';
    //         throw err;
    //     }
    // }



    async createQuestionPaper(req: CreateQuestionPaperReq): Promise<CommonResponse> {
        const transactionalEntityManager = new GenericTransactionManager(this.dataSource);
        try {
            const entitiesToSave = [];
            const { randomUUID } = new ShortUniqueId({ length: 10 });
            const paperBundlerId = randomUUID(20);

            // Pre-calculate total paper marks from blueprint
            const totalPaperMarks = req.sectionQuestionMappings[0].sectionsMapping.reduce(
                (sum, sec) => sum + sec.weightage * sec.noOfQns, 0
            );

            console.log("===== PAPER GENERATION DEBUG START =====");
            console.log("Total Paper Marks:", totalPaperMarks);
            console.log("Number of Sets:", req.noOfSets);
            console.log("Generation Method:", req.generationMethod);

            const hasDifficulty = req.difficulty &&
                (req.difficulty.easy > 0 || req.difficulty.moderate > 0 || req.difficulty.difficult > 0);

            // Track questions used across all sets to prevent repetition
            const globalUsedQuestions = new Map<string, Set<number>>();
            const sectionQuestionPools = new Map<string, { qno: number; complexity: number }[]>();

            console.log("Difficulty Distribution Required:", {
                easy: req.difficulty?.easy,
                moderate: req.difficulty?.moderate,
                difficult: req.difficulty?.difficult
            });

            for (const set of Array.from({ length: req.noOfSets }, (_, index) => index + 1)) {
                console.log(`\n========== PROCESSING SET ${set} ==========`);

                // Track questions for current set only (for serial numbering)
                const setQuestionMap = new Map<number, { serialQno: number; qno: number }>();

                const setReq = JSON.parse(JSON.stringify(req));

                const sets = req.generationMethod === PaperGenMethodEnum.SYSTEM
                    ? this.adapter.getSectionMappingsOfSection(setReq.sectionQuestionMappings, 1)
                    : this.adapter.getSectionMappingsOfSection(setReq.sectionQuestionMappings, set);

                console.log(`Total Sections to Process for Set ${set}:`, sets.sectionsMapping.length);

                // --- Difficulty budget for this set ---
                let easyBudget = 0;
                let moderateBudget = 0;
                let hardBudget = 0;

                if (hasDifficulty) {
                    const dist = await this.distributeDifficulty(
                        totalPaperMarks,
                        req.difficulty.easy,
                        req.difficulty.moderate,
                        req.difficulty.difficult,
                    );
                    easyBudget = dist.easy;
                    moderateBudget = dist.moderate;
                    hardBudget = dist.difficult;
                    console.log(`Set ${set} Difficulty Budget:`, {
                        totalMarks: totalPaperMarks,
                        easy: { marks: easyBudget, percentage: ((easyBudget / totalPaperMarks) * 100).toFixed(2) + '%' },
                        moderate: { marks: moderateBudget, percentage: ((moderateBudget / totalPaperMarks) * 100).toFixed(2) + '%' },
                        hard: { marks: hardBudget, percentage: ((hardBudget / totalPaperMarks) * 100).toFixed(2) + '%' }
                    });
                }

                for (const sections of sets.sectionsMapping) {
                    console.log(`\n  --- Section Details ---`);
                    console.log(`    Section ID: ${sections.sectionId}`);
                    console.log(`    Subject Id: ${sections.subject}`);
                    console.log(`    Chapter: ${sections.chapter}`);
                    console.log(`    Sub Ability ID: ${sections.subAbilityId || 'Not specified'}`);
                    console.log(`    Questions Required: ${sections.noOfQns}`);
                    console.log(`    Marks per Question: ${sections.weightage}`);
                    console.log(`    Total Section Marks: ${sections.noOfQns * sections.weightage}`);
                    console.log(`    Section No: ${sections.sectionId || 'Not specified'}`);

                    if (req.generationMethod === PaperGenMethodEnum.SYSTEM) {
                        // Get or fetch questions for this section (cache them)
                        // const poolKey = `${sections.subject}_${sections.chapter}_${sections.subAbilityId}_${sections.weightage}`;

                        const logicalKey = `${sections.subject}_${sections.chapter}_${sections.subAbilityId || 'any'}_${sections.weightage}`;
                        const physicalKey = `${sections.subject}_${sections.chapter}_${sections.subAbilityId || 'any'}_${sections.weightage}`;


                        let availableQuestions = sectionQuestionPools.get(physicalKey);



                        if (!availableQuestions) {
                            console.log(`    Fetching questions for Chapter: ${sections.chapter}, SubAbility: ${sections.subAbilityId || 'Any'}`);
                            const questions = await this.getAllQuestions(sections, ['qno', 'complexity', 'ability']);
                            availableQuestions = questions.data;
                            sectionQuestionPools.set(physicalKey, availableQuestions);
                            console.log(`    Total Questions Available in Pool: ${availableQuestions.length}`);

                            // Log difficulty distribution in available questions
                            const difficultyCount = availableQuestions.reduce((acc, q) => {
                                if (q.complexity === this.DIFFICULTY_EASY) acc.easy++;
                                else if (q.complexity === this.DIFFICULTY_MODERATE) acc.moderate++;
                                else if (q.complexity === this.DIFFICULTY_HARD) acc.hard++;
                                return acc;
                            }, { easy: 0, moderate: 0, hard: 0 });
                            console.log(`    Difficulty Distribution in Available Questions: Easy: ${difficultyCount.easy}, Moderate: ${difficultyCount.moderate}, Hard: ${difficultyCount.hard}`);
                        } else {
                            console.log(`    Using cached question pool with ${availableQuestions.length} questions`);
                        }
                        console.log(`    Question Pool QNOs:`, availableQuestions.map(q => q.qno));
                        console.log(`    Question Pool Size: ${availableQuestions.length}`);

                        // Initialize global tracking for this section if not exists
                        if (!globalUsedQuestions.has(logicalKey)) {
                            globalUsedQuestions.set(logicalKey, new Set());
                        }
                        const usedInPreviousSets = globalUsedQuestions.get(logicalKey)!;
                        console.log(`    Used QNOs in previous sets:`, Array.from(usedInPreviousSets));

                        // Filter out questions already used in previous sets
                        const unusedQuestions = availableQuestions.filter(q => !usedInPreviousSets.has(q.qno));

                        // console.log(`    Questions available after removing used ones: ${unusedQuestions.length}`);
                        console.log(`    Unused QNOs Available:`, unusedQuestions.map(q => q.qno));
                        console.log(`    Unused Questions Count: ${unusedQuestions.length}`);
                        console.log(`    Already used in previous sets: ${usedInPreviousSets.size}`);

                        if (unusedQuestions.length < sections.noOfQns) {
                            console.error(`    ❌ INSUFFICIENT UNUSED QUESTIONS!`);
                            console.error(`    Section: ${sections.sectionId}`);
                            console.error(`    Chapter: ${sections.chapter}`);
                            console.error(`    Required Questions: ${sections.noOfQns}`);

                            console.error(`    Pool QNOs:`, availableQuestions.map(q => q.qno));
                            console.error(`    Used QNOs:`, Array.from(usedInPreviousSets));
                            console.error(`    Remaining Unused QNOs:`, unusedQuestions.map(q => q.qno));
                            throw new Error(
                                `Insufficient unused questions! Required: ${sections.noOfQns}, Available unused: ${unusedQuestions.length} ` +
                                `for Chapter: ${sections.chapter}, Marks: ${sections.weightage}, Sub Ability: ${sections.subAbilityId}`
                            );
                        }

                        const qnMarks = sections.weightage;

                        // Sort and select questions from UNUSED pool only
                        console.log(`    Current Budget Before Selection: Easy:${easyBudget}, Moderate:${moderateBudget}, Hard:${hardBudget}`);
                        const sorted = this.sortByDifficultyPriority(unusedQuestions, easyBudget, moderateBudget, hardBudget, hasDifficulty);
                        const shuffledWithinPriority = this.shuffleWithinPriorityGroups(sorted, easyBudget, moderateBudget, hardBudget, hasDifficulty);

                        const selectedForThisSet = new Set<number>();
                        const selectedQuestions: { qno: number, complexity: number, marks: number, ability: number }[] = [];

                        for (const q of shuffledWithinPriority) {
                            if (selectedForThisSet.size >= sections.noOfQns) break;

                            // Double-check not used in this set or previous sets
                            if (selectedForThisSet.has(q.qno) || usedInPreviousSets.has(q.qno)) {
                                console.log(`      ⏭️  Skipping Q${q.qno} (already used)`);
                                continue;
                            }

                            selectedForThisSet.add(q.qno);
                            selectedQuestions.push({
                                qno: q.qno,
                                complexity: q.complexity,
                                marks: qnMarks,
                                ability: (q as any).ability
                            });

                            setQuestionMap.set(q.qno, {
                                serialQno: setQuestionMap.size + 1,
                                qno: q.qno,
                            });

                            // Deduct from budget
                            if (hasDifficulty) {
                                if (q.complexity === this.DIFFICULTY_EASY) {
                                    easyBudget -= qnMarks;
                                    console.log(`      ✅ Selected Q${q.qno} (EASY) - Remaining Easy Budget: ${easyBudget}`);
                                } else if (q.complexity === this.DIFFICULTY_MODERATE) {
                                    moderateBudget -= qnMarks;
                                    console.log(`      ✅ Selected Q${q.qno} (MODERATE) - Remaining Moderate Budget: ${moderateBudget}`);
                                } else if (q.complexity === this.DIFFICULTY_HARD) {
                                    hardBudget -= qnMarks;
                                    console.log(`      ✅ Selected Q${q.qno} (HARD) - Remaining Hard Budget: ${hardBudget}`);
                                }
                            } else {
                                console.log(`      ✅ Selected Q${q.qno} (Complexity: ${q.complexity})`);
                            }
                        }

                        console.log(`    Selected ${selectedQuestions.length}/${sections.noOfQns} questions for this section`);
                        console.log(`    Selected Questions Summary:`, selectedQuestions);

                        if (selectedForThisSet.size < sections.noOfQns) {
                            console.error(`    ❌ FAILED TO SELECT ENOUGH QUESTIONS!`);
                            console.error(`       Selected: ${selectedForThisSet.size}, Required: ${sections.noOfQns}`);
                            console.error(`       Remaining Budgets - Easy:${easyBudget}, Moderate:${moderateBudget}, Hard:${hardBudget}`);
                            console.error(`       Available questions were:`, availableQuestions.map(q => ({ qno: q.qno, complexity: q.complexity })));
                            throw new Error(
                                `Not enough unused questions remaining to generate set ${set} for Chapter: ${sections.chapter}`
                            );
                        }

                        // Mark these questions as used for future sets
                        selectedForThisSet.forEach(qno => usedInPreviousSets.add(qno));

                        // sections.qnos = Array.from(selectedForThisSet).toString();
                        sections.qnos = Array.from(selectedForThisSet).join(',');
                        console.log(`    Final QNOs for this section: ${sections.qnos}`);
                        console.log(`    Total used questions so far for this section: ${usedInPreviousSets.size}`);

                    } else {
                        // MANUAL mode
                        console.log(`    MANUAL MODE - Using pre-specified QNOs: ${sections.qnos}`);
                        const qnos = sections.qnos ? sections.qnos.split(',') : [];

                        const logicalKey = `${sections.subject}_${sections.chapter}_${sections.subAbilityId}_${sections.weightage}`;

                        if (!globalUsedQuestions.has(logicalKey)) {
                            globalUsedQuestions.set(logicalKey, new Set());
                        }
                        const usedInPreviousSets = globalUsedQuestions.get(logicalKey)!

                        // Track only THIS section's qnos (not all accumulated ones)
                        const thisSectionQnos: number[] = [];

                        qnos.forEach((qno) => {
                            const qnoNumber = Number(qno);
                            if (!usedInPreviousSets.has(qnoNumber) && !setQuestionMap.has(qnoNumber)) {
                                setQuestionMap.set(qnoNumber, {
                                    serialQno: setQuestionMap.size + 1,
                                    qno: qnoNumber,
                                });
                                usedInPreviousSets.add(qnoNumber); // Mark as used across sets
                                thisSectionQnos.push(qnoNumber);
                                console.log(`      ✅ Added manual Q${qnoNumber} to set`);
                            } else {
                                console.log(`      ⚠️  Skipping Q${qnoNumber} - already used in previous set`);
                            }
                        });

                        // Only set this section's own qnos, NOT all accumulated qnos from setQuestionMap
                        sections.qnos = thisSectionQnos.join(',');
                        console.log(`    Final QNOs for this section: ${sections.qnos}`);
                    }
                }

                // Validate difficulty deviation
                if (hasDifficulty && req.generationMethod === PaperGenMethodEnum.SYSTEM) {
                    const tolerance = 16;
                    const easyDeviation = Math.abs(easyBudget);
                    const moderateDeviation = Math.abs(moderateBudget);
                    const hardDeviation = Math.abs(hardBudget);

                    console.log(`\n  Set ${set} Final Difficulty Deviation:`);
                    console.log(`    Easy Budget Remaining: ${easyBudget} (Deviation: ${easyDeviation})`);
                    console.log(`    Moderate Budget Remaining: ${moderateBudget} (Deviation: ${moderateDeviation})`);
                    console.log(`    Hard Budget Remaining: ${hardBudget} (Deviation: ${hardDeviation})`);
                    console.log(`    Tolerance: ±${tolerance} marks`);

                    if (easyDeviation > tolerance || moderateDeviation > tolerance || hardDeviation > tolerance) {
                        console.error(`    ❌ DIFFICULTY DEVIATION EXCEEDS TOLERANCE!`);
                        throw new Error(
                            `Set ${set} difficulty targets not satisfiable within ±${tolerance} marks tolerance. ` +
                            `Remaining budgets → easy:${easyBudget}, moderate:${moderateBudget}, hard:${hardBudget}`
                        );
                    } else {
                        console.log(`    ✅ Difficulty deviation within acceptable range`);
                    }
                }

                // Summary for this set
                console.log(`\n  Set ${set} Summary:`);
                console.log(`    Total Questions Selected: ${setQuestionMap.size}`);
                console.log(`    Question Numbers: ${Array.from(setQuestionMap.keys()).join(', ')}`);

                // 🔧 FIX: Update the original request's section mappings with the selected qnos
                // This ensures that when convertDtoToEntity is called, qnos is properly set
                const updatedSectionMappings = [];
                for (let i = 0; i < setReq.sectionQuestionMappings.length; i++) {
                    const mapping = setReq.sectionQuestionMappings[i];
                    const updatedSections = [];
                    for (let j = 0; j < mapping.sectionsMapping.length; j++) {
                        const section = mapping.sectionsMapping[j];
                        // Find the corresponding section from sets.sectionsMapping that has the updated qnos
                        const updatedSection = sets.sectionsMapping.find(
                            s => s.sectionId === section.sectionId &&
                                s.weightage === section.weightage &&
                                s.chapter === section.chapter &&
                                s.subAbilityId === section.subAbilityId
                        );
                        if (updatedSection && updatedSection.qnos) {
                            updatedSections.push({
                                ...section,
                                qnos: updatedSection.qnos
                            });
                        } else {
                            updatedSections.push(section);
                        }
                    }
                    updatedSectionMappings.push({
                        ...mapping,
                        sectionsMapping: updatedSections
                    });
                }

                // Create a copy of req for this set
                const setReqForEntity = { ...req };
                setReqForEntity.noOfSets = set;
                setReqForEntity.paperBundlerId = paperBundlerId;
                setReqForEntity.sectionQuestionMappings = updatedSectionMappings;

                const entity = this.adapter.convertDtoToEntity(
                    setReqForEntity,
                    Array.from(setQuestionMap.values()),
                );

                // Reverse-calculate difficulty percentages
                const allQnoNumbers = Array.from(setQuestionMap.keys());
                if (allQnoNumbers.length > 0) {
                    const questionsWithComplexity = await this.createQuestionRepo.find({
                        select: ['qno', 'complexity', 'marks'],
                        where: {
                            qno: In(allQnoNumbers),
                            subject: req.subject,
                        },
                    });

                    let totalMarksSum = 0;
                    let easyMarks = 0;
                    let moderateMarks = 0;
                    let hardMarks = 0;

                    questionsWithComplexity.forEach(q => {
                        const marks = q.marks || 0;
                        totalMarksSum += marks;
                        if (q.complexity === this.DIFFICULTY_EASY) {
                            easyMarks += marks;
                        } else if (q.complexity === this.DIFFICULTY_MODERATE) {
                            moderateMarks += marks;
                        } else if (q.complexity === this.DIFFICULTY_HARD) {
                            hardMarks += marks;
                        }
                    });

                    if (totalMarksSum > 0 && req.generationMethod === PaperGenMethodEnum.MANUAL) {
                        entity.easyPercentage = parseFloat(((easyMarks / totalMarksSum) * 100).toFixed(2));
                        entity.moderatePercentage = parseFloat(((moderateMarks / totalMarksSum) * 100).toFixed(2));
                        entity.hardPercentage = parseFloat(((hardMarks / totalMarksSum) * 100).toFixed(2));
                    }
                }

                entitiesToSave.push(entity);

                // --- Other language papers ---
                if (req.generateOtherLanguagePapers) {
                    console.log(`\n  Generating Other Language Papers for Set ${set}...`);
                    const subjectName = await this.subjectRepo.findOne({
                        select: ['id', 'subject', 'class'],
                        where: { id: req.subject },
                    });
                    console.log("subjectName", subjectName);

                    const result = await this.subjectRepo.getOtherLanguageSubjectIds(
                        subjectName.subject,
                        subjectName.class,
                        req.subject,
                    );

                    console.log(`    Found ${result.length} other languages to generate`);

                    for (const otherSubject of result) {
                        console.log(`    Processing Language: ${otherSubject.languageId}, Subject: ${otherSubject.subjectId}`);

                        const mainModelPaper = await this.modelPaperRepo.findOne({
                            where: { id: req.qnpModelId }
                        });

                        const otherModel = await this.modelPaperRepo.findOne({
                            where: {
                                class: subjectName.class,
                                subject: otherSubject.subjectId,
                                language: otherSubject.languageId,
                                mainModelPaperId: mainModelPaper?.mainModelPaperId,
                            },
                        });

                        if (!otherModel) {
                            console.log(`      ⚠️  No model paper found for this language, skipping`);
                            continue;
                        }

                        const otherSubReq: CreateQuestionPaperReq = JSON.parse(JSON.stringify(req));
                        otherSubReq.subject = otherSubject.subjectId;
                        otherSubReq.qnpModelId = otherModel.id;
                        otherSubReq.noOfSets = set;
                        otherSubReq.paperBundlerId = paperBundlerId;

                        // const sectionMappings = [...sets.sectionsMapping];
                        const sectionMappings = JSON.parse(JSON.stringify(sets.sectionsMapping));
                        let allQnosFound = true;
                        const otherLangSetQuestionMap = new Map<number, { serialQno: number; qno: number }>();

                        for (const sec of sectionMappings) {
                            const mainSection = await this.sectionWiseModelRepo.findOne({
                                where: { id: sec.sectionId },
                            });

                            console.log("other subject data", otherSubject)
                            console.log("seccccccccccccccccccccccc", sec)
                            const sectionId = await this.sectionWiseModelRepo.findOne({
                                where: {
                                    qnpModel: otherModel,
                                    eachQnWeightage: sec.weightage,
                                    sectionNo: mainSection?.sectionNo,
                                },
                            });

                            if (!sectionId) {
                                console.log(`      ❌ Section not found for other language`);
                                allQnosFound = false;
                                break;
                            }

                            // const qnoArray = sec.qnos.split(',');
                            const qnoArray = sec.qnos ? sec.qnos.split(',') : [];
                            console.log(`      Looking for questions in other language: ${qnoArray.join(', ')}`);
                            console.log("other subject ids", otherSubject.subjectId);

                            const otherLangQuestions = await this.createQuestionRepo.find({
                                where: {
                                    qno: In(qnoArray.map(q => parseInt(q))),
                                    subject: otherSubject.subjectId,
                                },
                                select: ['qno', 'complexity', 'marks', 'department']
                            });

                            if (otherLangQuestions.length === qnoArray.length) {
                                sec.className = otherLangQuestions[0].department;
                                sec.sectionId = sectionId.id;
                                sec.subject = otherSubject.subjectId;
                                sec.language = otherSubject.languageId;

                                // Map questions for other language
                                qnoArray.forEach((qno, index) => {
                                    const qnoNumber = parseInt(qno);
                                    otherLangSetQuestionMap.set(qnoNumber, {
                                        serialQno: index + 1,
                                        qno: qnoNumber,
                                    });
                                });

                                console.log(`      ✅ Found all ${qnoArray.length} questions in other language`);
                            } else {
                                console.log(`      ❌ Could not find all questions in other language. Found: ${otherLangQuestions.length}, Required: ${qnoArray.length}`);
                                allQnosFound = false;
                                break;
                            }
                        }

                        if (allQnosFound && otherLangSetQuestionMap.size > 0) {
                            otherSubReq.sectionQuestionMappings = [
                                new SetAndQuestionMappingsModel(1, sectionMappings),
                            ];
                            const otherEntity = this.adapter.convertDtoToEntity(
                                otherSubReq,
                                Array.from(otherLangSetQuestionMap.values()),
                            );

                            // Copy reverse-calculated difficulty and ability percentages from main paper entity
                            otherEntity.easyPercentage = entity.easyPercentage;
                            otherEntity.moderatePercentage = entity.moderatePercentage;
                            otherEntity.hardPercentage = entity.hardPercentage;

                            otherEntity.awarenessPercentage = entity.awarenessPercentage;
                            otherEntity.sensitivityPercentage = entity.sensitivityPercentage;
                            otherEntity.creativityPercentage = entity.creativityPercentage;

                            entitiesToSave.push(otherEntity);
                            console.log(`      ✅ Other language paper created successfully`);
                        } else {
                            console.log(`      ⚠️  Skipping other language paper due to missing questions`);
                        }
                    }
                }
            }

            console.log(`\n===== SAVING ${entitiesToSave.length} QUESTION PAPERS =====`);
            console.log("entities to save to table", entitiesToSave)
            const savedEntity = await this.questionPapersRepo.save(entitiesToSave);
            console.log("===== PAPER GENERATION DEBUG END =====\n");

            return new CommonResponse(true, 1234, 'Paper Generated Successfully', savedEntity);
        } catch (err) {
            console.error("❌ ERROR DURING PAPER GENERATION:", err);
            if (transactionalEntityManager) {
                await transactionalEntityManager.releaseTransaction();
            }
            throw err;
        }
    }

    private shuffleWithinPriorityGroups(
        sorted: { qno: number; complexity: number }[],
        easyBudget: number,
        moderateBudget: number,
        hardBudget: number,
        hasDifficulty: boolean,
    ): { qno: number; complexity: number }[] {
        if (!hasDifficulty) return this.shuffleArray(sorted);

        const groups = new Map<number, { qno: number; complexity: number }[]>();
        for (const q of sorted) {
            if (!groups.has(q.complexity)) groups.set(q.complexity, []);
            groups.get(q.complexity)!.push(q);
        }

        // Shuffle within each group
        for (const [key, group] of groups) {
            groups.set(key, this.shuffleArray(group));
        }

        // Re-order groups by budget priority (descending)
        const budgetOf = (complexity: number) => {
            if (complexity === this.DIFFICULTY_EASY) return easyBudget;
            if (complexity === this.DIFFICULTY_MODERATE) return moderateBudget;
            if (complexity === this.DIFFICULTY_HARD) return hardBudget;
            return 0;
        };

        const orderedComplexities = [...groups.keys()].sort((a, b) => budgetOf(b) - budgetOf(a));
        return orderedComplexities.flatMap((c) => groups.get(c) || []);
    }



    // async createQuestionPaper(req: CreateQuestionPaperReq): Promise<CommonResponse> {
    //     const transactionalEntityManager = new GenericTransactionManager(this.dataSource);
    //     try {
    //         const entitiesToSave = [];
    //         const { randomUUID } = new ShortUniqueId({ length: 10 });
    //         const paperBundlerId = randomUUID(20);
    //         //  * GLOBAL DIFFICULTY DISTRIBUTION CALCULATION
    //         const allSections = req.sectionQuestionMappings
    //             .flatMap(s => s.sectionsMapping);
    //         const totalMarks = allSections.reduce((sum, s) => sum + (s.noOfQns * s.weightage), 0);
    //         console.log("difficulty level in serrvice", req.difficulty)
    //         const difficulty = req.difficulty || {
    //             easy: 25,
    //             moderate: 50,
    //             difficult: 25
    //         };
    //         let easyMarksRemaining =
    //             Math.round(totalMarks * difficulty.easy / 100);
    //         let moderateMarksRemaining =
    //             Math.round(totalMarks * difficulty.moderate / 100);
    //         let difficultMarksRemaining =
    //             Math.round(totalMarks * difficulty.difficult / 100);

    //         //  * SET LOOP
    //         for (const set of Array.from({ length: req.noOfSets }, (_, index) => index + 1)) {
    //             const questionAndSerialNumbersMap = new Map();
    //             const sets =
    //                 req.generationMethod === PaperGenMethodEnum.SYSTEM ? this.adapter.getSectionMappingsOfSection(req.sectionQuestionMappings, 1) : this.adapter.getSectionMappingsOfSection(req.sectionQuestionMappings, set);
    //             for (const sections of sets.sectionsMapping) {
    //                 console.log("sections,", sections)
    //                 //  * SYSTEM GENERATED PAPERS
    //                 if (req.generationMethod === PaperGenMethodEnum.SYSTEM) {
    //                     const questionResult = await this.getAllQuestions(sections, ['qno', 'complexity']);
    //                     const questions = this.shuffleArray(questionResult.data);
    //                     const result = new Set<number>();
    //                     if (questions.length < sections.noOfQns) {
    //                         throw new Error(`Insufficient questions! Required: ${sections.noOfQns}, Available: ${questions.length}`);
    //                     }
    //                     for (const question of questions) {
    //                         if (result.size >= sections.noOfQns) break;
    //                         const qno = question.qno;
    //                         const difficultyLevel = question.complexity;
    //                         const marks = sections.weightage;
    //                         if (
    //                             difficultyLevel === 'EASY' &&
    //                             easyMarksRemaining >= marks
    //                         ) {
    //                             easyMarksRemaining -= marks;
    //                         }
    //                         else if (
    //                             difficultyLevel === 'MODERATE' &&
    //                             moderateMarksRemaining >= marks
    //                         ) {
    //                             moderateMarksRemaining -= marks;
    //                         }
    //                         else if (
    //                             difficultyLevel === 'DIFFICULT' &&
    //                             difficultMarksRemaining >= marks
    //                         ) {
    //                             difficultMarksRemaining -= marks;
    //                         }
    //                     }
    //                     console.log("result.sizeeee", result.size);
    //                     console.log("sections.qnos", sections.qnos)
    //                     if (result.size < sections.noOfQns) {
    //                         throw new Error(
    //                             `Unable to satisfy difficulty distribution for section ${sections.sectionId}`
    //                         );
    //                     }
    //                     sections.qnos = Array.from(result).toString();
    //                 }
    //                 //  * MANUAL GENERATION
    //                 else {
    //                     const qnos = sections.qnos.split(',');
    //                     qnos.forEach((qno) => {
    //                         const qnoNumber = Number(qno);
    //                         if (!questionAndSerialNumbersMap.has(qnoNumber)) {
    //                             questionAndSerialNumbersMap.set(qnoNumber, {
    //                                 serialQno:
    //                                     questionAndSerialNumbersMap.size + 1,
    //                                 qno: qnoNumber
    //                             });
    //                         }
    //                     });
    //                 }
    //             }
    //             //  * EXISTING CODE (UNCHANGED)
    //             req.noOfSets = set;
    //             req.paperBundlerId = paperBundlerId;
    //             const entity = this.adapter.convertDtoToEntity(
    //                 req,
    //                 Array.from(questionAndSerialNumbersMap.values())
    //             );
    //             entitiesToSave.push(entity);
    //             //  * LANGUAGE PAPER GENERATION (UNCHANGED)
    //             if (req.generateOtherLanguagePapers) {
    //                 const subjectName = await this.subjectRepo.findOne({
    //                     select: ['id', 'subject', 'class'],
    //                     where: { id: req.subject },
    //                 });
    //                 const result =
    //                     await this.subjectRepo.getOtherLanguageSubjectIds(
    //                         subjectName.subject,
    //                         subjectName.class,
    //                         req.subject,
    //                     );
    //                 const mainModelPaperIdData =
    //                     await this.modelPaperRepo.findOne({
    //                         where: { id: req.qnpModelId },
    //                     });
    //                 for (const otherSubject of result) {
    //                     const allQnosSet = new Set();
    //                     const otherModel =
    //                         await this.modelPaperRepo.findOne({
    //                             where: {
    //                                 class: subjectName.class,
    //                                 subject: otherSubject.subjectId,
    //                                 language: otherSubject.languageId,
    //                                 mainModelPaperId:
    //                                     mainModelPaperIdData.mainModelPaperId
    //                             }
    //                         });
    //                     const otherSubReq: CreateQuestionPaperReq =
    //                         JSON.parse(JSON.stringify(req));
    //                     otherSubReq.subject = otherSubject.subjectId;
    //                     otherSubReq.qnpModelId = otherModel.id;
    //                     const sectionMappings = [...sets.sectionsMapping];
    //                     for (const sec of sectionMappings) {
    //                         const mainSection =
    //                             await this.sectionWiseModelRepo.findOne({
    //                                 where: { id: sec.sectionId }
    //                             });
    //                         const sectionId =
    //                             await this.sectionWiseModelRepo.findOne({
    //                                 where: {
    //                                     qnpModel: otherModel,
    //                                     eachQnWeightage: sec.weightage,
    //                                     sectionNo: mainSection.sectionNo
    //                                 }
    //                             });
    //                         const qnoArray = sec.qnos.split(',');
    //                         const result =
    //                             await this.createQuestionRepo.find({
    //                                 where: {
    //                                     qno: In([null, ...qnoArray]),
    //                                     subject: otherSubject.subjectId,
    //                                     ...(sec.subAbilityId && {
    //                                         abilityType: sec.subAbilityId
    //                                     })
    //                                 }
    //                             });
    //                         if (result.length === qnoArray.length) {
    //                             sec.chapter = result[0].chapter;
    //                             sec.className = result[0].class;
    //                             sec.sectionId = sectionId.id;
    //                             sec.subject = otherSubject.subjectId;
    //                             sec.language = otherSubject.languageId;
    //                             allQnosSet.add(
    //                                 result.length === qnoArray.length
    //                             );
    //                         }
    //                     }
    //                     otherSubReq.sectionQuestionMappings = [new SetAndQuestionMappingsModel(1, sectionMappings)];
    //                     if (allQnosSet.size === 1 && allQnosSet.has(true)) {
    //                         const entity =
    //                             this.adapter.convertDtoToEntity(
    //                                 otherSubReq,
    //                                 Array.from(
    //                                     questionAndSerialNumbersMap.values()
    //                                 )
    //                             );
    //                         entitiesToSave.push(entity);
    //                     }
    //                 }
    //             }
    //         }
    //         const savedEntity =
    //             await this.questionPapersRepo.save(entitiesToSave);
    //         return new CommonResponse(true, 1234, 'Paper Generated Successfully', savedEntity);
    //     } catch (err) {
    //         transactionalEntityManager
    //             ? await transactionalEntityManager.releaseTransaction()
    //             : '';
    //         throw err;
    //     }
    // }

    // async createQuestionPaper(req: CreateQuestionPaperReq): Promise<CommonResponse> {
    //     const transactionalEntityManager = new GenericTransactionManager(this.dataSource);
    //     try {
    //         const entitiesToSave = [];
    //         const { randomUUID } = new ShortUniqueId({ length: 10 });
    //         const paperBundlerId = randomUUID(20);
    //         const usedQuestionsGlobal = new Set<number>();
    //         for (const set of Array.from({ length: req.noOfSets }, (_, index) => index + 1)) {
    //             const questionAndSerialNumbersMap = new Map();
    //             const sets = req.generationMethod === PaperGenMethodEnum.SYSTEM ? this.adapter.getSectionMappingsOfSection(req.sectionQuestionMappings, 1) : this.adapter.getSectionMappingsOfSection(req.sectionQuestionMappings, set);

    //             for (const sections of sets.sectionsMapping) {
    //                 if (req.generationMethod === PaperGenMethodEnum.SYSTEM) {
    //                     const questions = await this.getAllQuestions(sections, ['qno']);
    //                     const questionsIds = this.shuffleArray(questions.data.map((rec) => rec.qno));
    //                     const result = new Set<number>();
    //                     console.log(`Required: ${sections.noOfQns}, Available: ${questionsIds.length}`);

    //                     if (questionsIds.length < sections.noOfQns) {
    //                         throw new Error(`Insufficient questions! Required: ${sections.noOfQns}, Available: ${questionsIds.length} for Chapter: ${sections.chapter}, Marks: ${sections.weightage}, SubAbilityId: ${sections.subAbilityId || 'Any'}`,);
    //                     }
    //                     let index = 0;
    //                     while (result.size < sections.noOfQns && index < questionsIds.length) {
    //                         const qno = questionsIds[index];
    //                         if (!result.has(qno) && !questionAndSerialNumbersMap.has(qno) && !usedQuestionsGlobal.has(qno)) {
    //                             result.add(qno);
    //                             questionAndSerialNumbersMap.set(qno, {
    //                                 serialQno: questionAndSerialNumbersMap.size + 1,
    //                                 qno: qno,
    //                             });
    //                             usedQuestionsGlobal.add(qno);
    //                         }
    //                         index++;
    //                     }
    //                     console.log('reeeeee', result.size, 'seeeeeeee', sections.noOfQns);

    //                     if (result.size < sections.noOfQns) {
    //                         throw new Error(`Not enough unused questions remaining to generate set ${set}`);
    //                     }
    //                     sections.qnos = Array.from(result).toString();
    //                 } else {
    //                     const qnos = sections.qnos.split(',');
    //                     qnos.forEach((qno) => {
    //                         const qnoNumber = Number(qno);
    //                         if (!questionAndSerialNumbersMap.has(qnoNumber) &&
    //                             !usedQuestionsGlobal.has(qnoNumber)) {
    //                             questionAndSerialNumbersMap.set(qnoNumber, { serialQno: questionAndSerialNumbersMap.size + 1, qno: qnoNumber });
    //                             usedQuestionsGlobal.add(qnoNumber);
    //                         }
    //                     });
    //                 }
    //             }

    //             req.noOfSets = set;
    //             req.paperBundlerId = paperBundlerId;
    //             const entity = this.adapter.convertDtoToEntity(req, Array.from(questionAndSerialNumbersMap.values()),);
    //             entitiesToSave.push(entity);
    //             if (req.generateOtherLanguagePapers) {
    //                 const subjectName = await this.subjectRepo.findOne({
    //                     select: ['id', 'subject', 'class'], where: { id: req.subject },
    //                 });
    //                 const result = await this.subjectRepo.getOtherLanguageSubjectIds(
    //                     subjectName.subject,
    //                     subjectName.class,
    //                     req.subject,
    //                 );
    //                 const mainModelPaperIdData = await this.modelPaperRepo.findOne({ where: { id: req.qnpModelId }, });
    //                 for (const otherSubject of result) {
    //                     const allQnosSet = new Set();
    //                     const otherModel = await this.modelPaperRepo.findOne({
    //                         where: { class: subjectName.class, subject: otherSubject.subjectId, language: otherSubject.languageId, mainModelPaperId: mainModelPaperIdData.mainModelPaperId }
    //                     });
    //                     const otherSubReq: CreateQuestionPaperReq = JSON.parse(
    //                         JSON.stringify(req));
    //                     otherSubReq.subject = otherSubject.subjectId;
    //                     otherSubReq.qnpModelId = otherModel.id;
    //                     const sectionMappings = [...sets.sectionsMapping];
    //                     for (const sec of sectionMappings) {
    //                         const mainQnpModel = new QuestionPaperModal();
    //                         mainQnpModel.id = req.qnpModelId;
    //                         const mainSection = await this.sectionWiseModelRepo.findOne({ where: { id: sec.sectionId } });
    //                         const sectionId = await this.sectionWiseModelRepo.findOne({ where: { qnpModel: otherModel, eachQnWeightage: sec.weightage, sectionNo: mainSection.sectionNo, } });
    //                         const qnoArray = sec.qnos.split(',');
    //                         const result = await this.createQuestionRepo.find({
    //                             where: { qno: In([null, ...qnoArray]), subject: otherSubject.subjectId, ...(sec.subAbilityId && { abilityType: sec.subAbilityId }) }
    //                         });
    //                         if (result.length === qnoArray.length) {
    //                             sec.chapter = result[0].chapter;
    //                             sec.className = result[0].class;
    //                             sec.sectionId = sectionId.id;
    //                             sec.subject = otherSubject.subjectId;
    //                             sec.language = otherSubject.languageId;
    //                             allQnosSet.add(result.length === qnoArray.length);
    //                         }
    //                     }

    //                     otherSubReq.sectionQuestionMappings = [new SetAndQuestionMappingsModel(1, sectionMappings),];
    //                     if (allQnosSet.size === 1 && allQnosSet.has(true)) {
    //                         const entity = this.adapter.convertDtoToEntity(
    //                             otherSubReq, Array.from(questionAndSerialNumbersMap.values()));
    //                         entitiesToSave.push(entity);
    //                     }
    //                 }
    //             }
    //         }
    //         const savedEntity = await this.questionPapersRepo.save(entitiesToSave);
    //         return new CommonResponse(true, 1234, 'Paper Generated Successfully', savedEntity,);
    //     } catch (err) {
    //         transactionalEntityManager ? await transactionalEntityManager.releaseTransaction() : '';
    //         throw err;
    //     }
    // }

    async getAllQuestionPapers(req: qPRequestModel): Promise<CommonResponse> {
        const savedEntities = await this.questionPapersRepo.getAllQuestionPapers(req);
        return new CommonResponse(true, 123, 'All Questions retrieved Successfully', savedEntities)
    }

    async getAllQuestionPapersToMerge(req: QPaperMergeGerReqModel): Promise<CommonResponse> {
        const mainModelPaperIdData = await this.modelPaperRepo.find({ where: { subject: In([...req.subjectIds]) } });
        console.log(mainModelPaperIdData);

        const mainModelPaperIds = Array.from(new Set(mainModelPaperIdData.map(item => item.mainModelPaperId).filter(rec => rec !== null)))
        if (mainModelPaperIds.length === 0) throw new ErrorResponse(123, 'No Papers found to retrieved');
        const savedEntities = await this.questionPapersRepo.getAllQuestionPapersToMerge(req, mainModelPaperIds);
        return new CommonResponse(true, 123, 'All Questions retrieved Successfully', savedEntities)
    }

    async getAllQuestionsByIds(req: QnosReq): Promise<CommonResponse> {
        const result = await this.createQuestionRepo.find({ where: { id: In([null, ...req.qnos]) } });
        return new CommonResponse(true, 123, 'All Questions retrieved Successfully', result)
    }

    async getQuestionPaperDataById(req: QNPIdReq): Promise<CommonResponse> {
        const sectionWiseMap: Map<number, Map<number, number[]>> = new Map();
        const sectionDataMap: Map<number, { mandatory_qns: number, each_qn_weightage: number, section_marks: number, note: string }> = new Map();
        const data = await this.questionPapersRepo.getQuestionPaperDataById(req);
        for (const sectionLevelData of data) {
            if (!sectionWiseMap.has(sectionLevelData.section))
                sectionWiseMap.set(sectionLevelData.section, new Map());
            if (!sectionWiseMap.get(sectionLevelData.section).has(sectionLevelData.chapter))
                sectionWiseMap.get(sectionLevelData.section).set(sectionLevelData.chapter, sectionLevelData.qno_s?.split(','));
            else {
                sectionWiseMap.get(sectionLevelData.section).get(sectionLevelData.chapter).push(...sectionLevelData.qno_s?.split(','));
            }
            sectionDataMap.set(sectionLevelData.section, {
                mandatory_qns: sectionLevelData.mandatory_qns,
                each_qn_weightage: sectionLevelData.each_qn_weightage,
                section_marks: sectionLevelData.section_marks,
                note: sectionLevelData.note
            });
        }
        if (sectionWiseMap.size) {
            const res = new PaperPreviewModel(data[0].class_name, data[0].subject_name, data[0].no_of_sections, data[0].marks, data[0].total_questions, data[0].time_limit, data[0].tagline, data[0].setno, data[0].language_name, data[0].instructions, [], data[0].qnos_order ? JSON.parse(data[0].qnos_order) : []);
            for (const [section, sectionChapterMap] of sectionWiseMap.entries()) {
                const sectionData = sectionDataMap.get(section);
                const sectionsData = new PaperPreviewSectionsModel(sectionData.mandatory_qns, sectionData.each_qn_weightage, sectionData.section_marks, sectionData.note, []);
                for (const [chapter, qnos] of sectionChapterMap.entries()) {
                    // const result = (await this.createQuestionRepo.find({ select: ['qno', 'question'], where: { qno: In([null, Array.from(new Set(qnos))]), subject: data[0].subject, class: data[0].class, chapter: chapter } })).map(rec => new QuestionsModel(rec.qno, rec.question));
                    const fetchedQuestions = await this.createQuestionRepo.find({
                        select: ['qno', 'question'],
                        where: { qno: In([null, ...Array.from(new Set(qnos))]), subject: data[0].subject, department: data[0].class }
                    });

                    // preserve the exact order from qnos array
                    const orderedQuestions = qnos
                        .map(qno => fetchedQuestions.find(q => q.qno === String(qno)))
                        .filter(Boolean) // remove nulls
                        .map(rec => new QuestionsModel(rec.qno, rec.question));
                    sectionsData.questions.push(...orderedQuestions)
                }
                res.sections.push(sectionsData);
            }
            return new CommonResponse(true, 1235, 'All Questions retrieved Successfully', res)
        } else {
            return new CommonResponse(false, 1235, 'All Questions retrieved Successfully')
        }
    }


    async isDefaultModelPaperExist(req: DefaultModelPaperCheckReqModel) {
        const subjectName = await this.subjectRepo.findOne({ select: ['id', 'subject'], where: { id: req.subjectId } });
        const result = await this.subjectRepo.getOtherLanguageSubjectIds(subjectName.subject, req.classId, req.subjectId);
        const languages = []
        const modelPaperData = await this.modelPaperRepo.findOne({ where: { mainModelPaperId: req.mainModelPaperId } })
        for (const subjectLanguage of result) {
            const count = await this.modelPaperRepo.count({ where: { class: req.classId, subject: subjectLanguage.subjectId, language: subjectLanguage.languageId, mainModelPaperId: modelPaperData.mainModelPaperId } });
            if (!count) {
                languages.push(subjectLanguage.language)
            }
        }
        if (languages.length) {
            throw new ErrorResponse(123, `Languages ${languages.toString()} doesn't have default modal`);
        } else {
            return new CommonResponse(true, 1234, 'Languages have default modal');
        }
    }
    async clonePaperToOtherLanguage(req: ClonePaperReq) {

        return new CommonResponse(false, 1235, 'Paper Cloned Successfully');
    }


    async validateOtp(req: QNPIdReq): Promise<CommonResponse> {
        const data = await this.questionPapersRepo.findOne({ select: ['otp'], where: { id: req.qnpId } });
        return new CommonResponse(false, 1235, 'All Questions retrieved Successfully');
    }

    generateOTP() {
        const otpLength = 9;
        const minDigit = 0;
        const maxDigit = 9;
        let otp = '';
        for (let i = 0; i < otpLength; i++) {
            const randomDigit = Math.floor(Math.random() * (maxDigit - minDigit + 1)) + minDigit;
            otp += randomDigit.toString();
        }
        return otp;
    }


    async generateOtp(req: QNPIdReq): Promise<CommonResponse> {
        //     const otp = this.generateOTP();

        //     const currentDate = new Date();
        //     const formattedDate = currentDate.toLocaleString();

        //     const data = await this.questionPapersRepo.update({ id: req.qnpId }, { otp });

        //     const mobileNums = ['7013647957'];
        //     const messageStatuses = [];

        //     const otpTemplate = `
        // 📅 ${formattedDate}
        // 🔐 Your One-Time Password (OTP) is: ${otp} 🔑
        // ⏳ This OTP is valid for 5 minutes. ⏳
        // ❗️ Do not share your OTP with anyone. ❗️
        // 🚫 If you did not request this OTP, please contact customer support. 🚫
        //     `;

        //     for (const contact of mobileNums) {
        //         const parameters = [
        //             { name: 'otp', value: otpTemplate },
        //         ];
        //         const req = await new MessageParameters(contact, otpTemplate, parameters, 'en_us');

        //         const messageStatus = await this.wtsAppMessage.sendMessageThroughFbApi(req);
        //         if (messageStatus.status) {
        //             messageStatuses.push(messageStatus);
        //         }
        //     }

        return new CommonResponse(true, 11108, 'OTP generated successfully');
    }

    async getSubjectsByClassId(req: ClassRequestModel): Promise<CommonResponse> {
        const result = await this.subjectRepo.getSubjectsByClassId(req);
        return new CommonResponse(true, 124, 'Subjects retrieved successfully', result);
    }

    async getBaseSubjectsDropDown(req: ClassRequestModel): Promise<CommonResponse> {
        const result = await this.subjectRepo.getBaseSubjectsDropDown(req);
        console.log(result, 'result');

        return new CommonResponse(true, 124, 'Subjects retrieved successfully', result);
    }

    async getOtherLanguageSubjects(req: SubjectClassReqModel): Promise<CommonResponse> {
        try {
            const subjectName = await this.subjectRepo.findOne({
                select: ['id', 'subject', 'class', 'language'],
                where: { id: req.subject },
            });
            const result = await this.subjectRepo.getOtherLanguageSubjectIds(subjectName.subject, subjectName.class, req.subject);
            return new CommonResponse(true, 1234, 'Other language Subjects Retrieved Successfully', result);
        } catch (err) {
            throw err;
        }
    }

    async getAllSubjectIdsForPreview(req: SubjectClassReqModel): Promise<CommonResponse> {
        try {
            const subjectName = await this.subjectRepo.findOne({
                select: ['id', 'subject', 'class', 'language'],
                where: { id: req.subject },
            });
            const result = await this.subjectRepo.getAllSubjectIdsForPreview(subjectName.subject, subjectName.class, req.subject);
            return new CommonResponse(true, 1234, 'Other language Subjects Retrieved Successfully', result);
        } catch (err) {
            throw err;
        }
    }

    async getOtherLanguageQuestions(req: CreateQuestionPaperReq): Promise<CommonResponse> {
        try {
            const subjectName = await this.subjectRepo.findOne({
                select: ['id', 'subject', 'class'],
                where: { id: req.subject },
            });

            const questionAndSerialNumbersMap = new Map();
            const sets = this.adapter.getSectionMappingsOfSection(req.sectionQuestionMappings, 1);
            let sectionWiseMap = new Map();
            for (const sections of sets.sectionsMapping) {
                const qnos = sections.qnos.split(',');
                qnos.forEach((qno) => {
                    if (!questionAndSerialNumbersMap.has(qno)) {
                        questionAndSerialNumbersMap.set(qno, { serialQno: questionAndSerialNumbersMap.size, qno: Number(qno) });
                    }
                });
                if (!sectionWiseMap.has(sections.sectionId))
                    sectionWiseMap.set(sections.sectionId, { ...sections, sectionWiseQuestions: sections.qnos?.split(',') });
                else {
                    sectionWiseMap.get(sections.sectionId).sectionWiseQuestions.push(...sections.qnos?.split(','));
                }
            }
            sectionWiseMap = new Map(
                [...sectionWiseMap.entries()].sort((a, b) => a[0] - b[0])
            );

            const result = await this.subjectRepo.getAllSubjectIdsForPreview(subjectName.subject, subjectName.class, req.subject);
            const questions = [];
            const qnpModelData = await this.modelPaperRepo.findOne({ where: { id: req.qnpModelId } });
            for (const otherSubject of result) {
                const allQnosSet = new Set();
                const otherModel = await this.modelPaperRepo.findOne({ where: { class: subjectName.class, subject: otherSubject.subjectId, language: otherSubject.languageId, mainModelPaperId: qnpModelData.mainModelPaperId } });
                const otherSubReq: CreateQuestionPaperReq = JSON.parse(JSON.stringify(req));
                otherSubReq.subject = otherSubject.subjectId;
                otherSubReq.qnpModelId = otherModel.id;
                const className = await this.classRepo.findOne({ where: { id: subjectName.class } });
                const subjectNames = await this.subjectRepo.findOne({ where: { id: otherSubject.subjectId } });
                const languageName = await this.languagesRepo.findOne({ where: { id: otherSubject.languageId } });

                const res = new PaperPreviewModel(className.class, subjectNames.subject, otherModel.noOfSections, otherModel.marks, otherModel.totalQuestions, otherModel.timeLimit, otherModel.tagline, '1', languageName.language, otherModel.instructions, [], Array.from(questionAndSerialNumbersMap.values()));

                for (const [section, sectionData] of sectionWiseMap.entries()) {
                    const mainSection = await this.sectionWiseModelRepo.findOne({ where: { id: section } });
                    const sectionId = await this.sectionWiseModelRepo.findOne({ where: { qnpModel: otherModel, eachQnWeightage: mainSection.eachQnWeightage, sectionNo: mainSection.sectionNo } });
                    // const result = (await this.createQuestionRepo.find({ select: ['qno', 'question'], where: { qno: In([null, ...sectionData.sectionWiseQuestions]), subject: otherSubject.subjectId, class: subjectName.class } })).map(rec => new QuestionsModel(rec.qno, rec.question));
                    // const sectionsData = new PaperPreviewSectionsModel(sectionId.mandatoryQns, sectionId.eachQnWeightage, sectionId.marks, sectionId.note, result);
                    const fetchedQuestions = await this.createQuestionRepo.find({ select: ['qno', 'question'], where: { qno: In([null, ...sectionData.sectionWiseQuestions]), subject: otherSubject.subjectId, department: subjectName.class } });
                    // const orderedQuestions = sectionData.sectionWiseQuestions.map(qno => { return fetchedQuestions.find(q => q.qno === String(qno));}).filter(Boolean).map(rec => new QuestionsModel(rec.qno, rec.question));
                    const orderedQuestions = sectionData.sectionWiseQuestions
                        .map(qno => fetchedQuestions.find(q => q.qno === String(qno)))
                        .filter(Boolean)
                        .sort((a, b) => sectionData.sectionWiseQuestions.indexOf(a.qno.toString()) -
                            sectionData.sectionWiseQuestions.indexOf(b.qno.toString()))
                        .map(rec => new QuestionsModel(rec.qno, rec.question));
                    const sectionsData = new PaperPreviewSectionsModel(sectionId.mandatoryQns, sectionId.eachQnWeightage, sectionId.marks, sectionId.note, orderedQuestions);
                    res.sections.push(sectionsData);
                }
                questions.push(res);
            }
            return new CommonResponse(true, 1234, 'Other language Questions Retrieved Successfully', questions);
        } catch (err) {
            throw err;
        }
    }

    async userQnoMapInsert() {
        const users = ['biesecretary@bie.com', 'biecto@bie.com', 'bieap_user2@bie.com', 'bieap_user1@bie.com', 'bieap_user3@bie.com'];
        for (const user of users) {
            const questions = await this.createQuestionRepo.find({ select: ['id'] })
            const questionsIds = this.shuffleArray(questions.map(rec => rec.id));
            const result = new Set<number>();
            while (result.size != questions.length) {
                const randomInt = Math.floor(Math.random() * (questionsIds.length - 0 + 1)) + 0;
                if (questionsIds[randomInt]) {
                    if (!result.has(questionsIds[randomInt])) {
                        result.add(questionsIds[randomInt]);
                    }
                }
            }
            const entitiesArray = [];
            for (const qno of Array.from(result)) {
                const entity = new UserToQnosMappingEntity();
                entitiesArray.push(entity)
            }

        }

    }




    async savePaperMergeData(req: QPaperMergeCreateReqModel): Promise<CommonResponse> {
        const paperMerge = new PaperMergeEntity();
        paperMerge.id = req.id;
        paperMerge.noOfParts = req.noOfParts;
        paperMerge.paperName = req.paperName;
        paperMerge.classId = req.classId;
        const paperMergeChildData = []
        for (const part of req.paperMergeChild) {
            const paperMergeChild = new PaperMergeChildEntity();
            paperMergeChild.paperId = part.paperId;
            paperMergeChild.partId = part.partId;
            paperMergeChild.paperMergeId = paperMerge;
            paperMergeChild.mainModelPaperId = part.mainModelPaperId;
            paperMergeChildData.push(paperMergeChild);
        }
        paperMerge.paperMergeChild = paperMergeChildData;
        await this.paperMergeRepo.save(paperMerge);
        return new CommonResponse(true, 1234, 'Paper Merge Created Successfully');
    }

    async getAllPaperMergeData(req: ClassIdRequestModel): Promise<CommonResponse> {
        try {

            const result = await this.paperMergeRepo.getMergeData(req);

            const paperIdsMap = new Map<number, Map<string, Map<number, any>>>();
            const mergeMap = new Map<number, any>()
            const paperIds = new Map<number, Set<number>>();
            for (const merge of result) {
                for (const child of merge.paperMergeChild) {
                    const data = await this.questionPapersRepo.getAllQuestionPapersOfGivenBundlePaperId(req.classId, child.paperId);
                    for (const dataItem of data) {
                        if (!paperIdsMap.has(merge.id)) {
                            paperIdsMap.set(merge.id, new Map<string, Map<number, any>>());
                        }
                        if (!paperIdsMap.get(merge.id).has(dataItem.language)) {
                            paperIdsMap.get(merge.id).set(dataItem.language, new Map<number, any>());
                        }
                        paperIdsMap.get(merge.id).get(dataItem.language).set(child.paperId, { ...dataItem, paperMergeId: merge.id, paperName: merge.paperName, noOfParts: merge.noOfParts });
                        if (!paperIds.has(merge.id)) {
                            paperIds.set(merge.id, new Set<number>())
                        }
                        paperIds.get(merge.id).add(child.paperId)
                    }
                    if (!mergeMap.has(merge.id)) {
                        const mergeClone = { ...merge };
                        delete mergeClone.paperMergeChild
                        mergeMap.set(merge.id, merge)
                    }
                }
            }
            console.log(paperIdsMap, 'paperIdsMap');
            console.log(mergeMap, 'mergeMap');


            const languageWiseGroupData = []
            for (const [merge, mergeData] of mergeMap.entries()) {
                const paperData = { ...mergeData };
                delete paperData.paperMergeChild
                paperData['languageWiseData'] = [];
                for (const [language, paperIdMap] of paperIdsMap.get(merge)) {
                    const languageWiseData = {
                        language: language,
                        data: []
                    }
                    for (const paperId of Array.from(paperIds.get(merge))) {
                        languageWiseData.data.push(paperIdMap.get(paperId))
                    }
                    paperData['languageWiseData'].push(languageWiseData);
                }
                languageWiseGroupData.push(paperData);
            }


            return new CommonResponse(true, 1234, 'Paper Merge Data Fetched Successfully', languageWiseGroupData);
        } catch (error) {
            console.log(error);

            throw error
        }
    }

    async getDistinctSubjectsForLogin(): Promise<CommonResponse> {
        const result = await this.subjectRepo.getDistinctSubjectsRepo();
        return new CommonResponse(true, 123, 'Languages retrieved successfully', result);
    }
}